import { endsWith } from 'ramda';
import { z } from 'zod';

import { commonErrors } from '@utils/error';
import { doesNotExist } from '@utils/general';
import { isSelected, optionalString, superRefineHelper } from '@utils/validation';

export const dataConstants = {
	ROUTE: 'testosteroneRouteType',
	MEDICATION: 'testosteroneMedicationType',
	TYPE: 'testosteroneInjectableType',
	CUSTOM: 'testosteroneDoseTypeCustom',
};

export const schema = z
	.object({
		[dataConstants.ROUTE]: optionalString,
		[dataConstants.MEDICATION]: optionalString,
		[dataConstants.TYPE]: optionalString,
		[dataConstants.CUSTOM]: optionalString,
	})
	.superRefine((data, ctx) => {
		const commonParams = { data: data, context: ctx };
		const routeSelected = isSelected(data?.[dataConstants.ROUTE] ?? '');
		const medicationSelected = isSelected(data?.[dataConstants.MEDICATION] ?? '');
		const typeSelected = isSelected(data?.[dataConstants.TYPE] ?? '');
		const isGel = endsWith('.gel.pump', data?.[dataConstants.ROUTE] ?? '');
		const isInjectable = endsWith('.injectable', data?.[dataConstants.ROUTE] ?? '');
		superRefineHelper({
			...commonParams,
			dataPath: dataConstants.ROUTE,
			negativeCondition: doesNotExist,
			message: commonErrors.selectionRequired,
		});
		if (routeSelected) {
			if (isInjectable) {
				superRefineHelper({
					...commonParams,
					dataPath: dataConstants.MEDICATION,
					negativeCondition: doesNotExist,
					message: commonErrors.selectionRequired,
				});
				if (medicationSelected) {
					superRefineHelper({
						...commonParams,
						dataPath: dataConstants.TYPE,
						negativeCondition: doesNotExist,
						message: commonErrors.selectionRequired,
					});
				}
			}
		}
	});

export const testMeds: string[] = [
	'hrt_t.gel.pump',
	'hrt_t.injectable.t_cypionate',
	'hrt_t.injectable.t_enanthate',
];

export const testContent = {
	page_header: "Do you already know what type, route, and dosage you'd like to take testosterone? ",
	page_details:
		'<p>If you don&rsquo;t already know this information, or would like a dose that is not listed here, no worries! Your clinician will help you figure it out during your video consultation.</p>',
	routes: {
		route_header: 'How would you like to take testosterone?',
		route_types: [
			{
				label: 'Injectable',
				description: 'Weekly or every-other week, self-administered hormone injection.',
				key: 'hrt_t.injectable',
			},
			{
				label: 'Gel',
				description: 'Massaged into the skin daily for absorption.',
				key: 'hrt_t.gel',
			},
			{
				label: "I don't know",
				description:
					"That's okay! You'll be able to meet with a clinician to discuss your available options",
				key: 'hrt_t.dont_know',
			},
		],
	},
	injectables: {
		injectable_medication_header: 'Injectable medication',
		injectable_type_header: 'Injection type',
		injectable_types: [
			{
				label: 'Subcutaneous',
				description:
					'Most common because it is a smaller needle & less painful. Inject into fatty tissue under the skin.',
				key: 'hrt_t.injectable.subq',
			},
			{
				label: 'Intramuscular',
				description:
					'Larger needle & deeper injection, with benefit of less frequent injection. Inject into muscle.',
				key: 'hrt_t.injectable.intramusc',
			},
			{
				label: "I don't know",
				description: '',
				key: 'hrt_t.injectable.dont_know',
			},
		],
		injectable_medications: [
			{
				label: 'Testosterone Cypionate',
				description: 'Most popular selection: easier to access, cheaper.',
				key: 'hrt_t.injectable.t_cypionate',
			},
			{
				label: 'Testosterone Enanthate',
				description: 'Recommended for people who have had negative reactions to Cypionate.',
				key: 'hrt_t.injectable.t_enanthate',
			},
			{
				label: "I don't know",
				description: '',
				key: 'hrt_t.injectable.dont_know',
			},
		],
	},
};
